import React, { Suspense, lazy, useEffect } from "react";
import "@/App.css";
import "@/config/axiosConfig";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { AdminProvider } from "@/context/AdminContext";
import { setupForegroundNotifications } from "@/utils/pushNotifications";

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-yellow-400 to-orange-500">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent mx-auto mb-4"></div>
      <p className="text-white font-medium">Loading...</p>
    </div>
  </div>
);

// Customer App Pages - Eagerly loaded (needed immediately)
import Splash from "@/pages/Splash";
import Login from "@/pages/Login";
import OTPVerification from "@/pages/OTPVerification";
import CitySelection from "@/pages/CitySelection";
import Home from "@/pages/Home";

// Customer App Pages - Lazy loaded
const VideoDealsScreen = lazy(() => import("@/pages/VideoDealsScreen"));
const OfferDetailPage = lazy(() => import("@/pages/OfferDetailPage"));
const ShopOffersListPage = lazy(() => import("@/pages/ShopOffersListPage"));
const ViewAllOffersPage = lazy(() => import("@/pages/ViewAllOffersPage"));
const SavedOffersPage = lazy(() => import("@/pages/SavedOffersPage"));
const RewardsPage = lazy(() => import("@/pages/RewardsPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const EventDealsPage = lazy(() => import("@/pages/EventDealsPage"));
const SearchResultsPage = lazy(() => import("@/pages/SearchResultsPage"));

// Captain (Merchant) App Pages - Lazy loaded
const CaptainLogin = lazy(() => import("@/pages/captain/CaptainLogin"));
const CaptainRegister = lazy(() => import("@/pages/captain/CaptainRegister"));
const CaptainOTPVerification = lazy(() => import("@/pages/captain/CaptainOTPVerification"));
const CaptainDashboard = lazy(() => import("@/pages/captain/CaptainDashboard"));
const CaptainProfilePage = lazy(() => import("@/pages/captain/CaptainProfilePage"));
const CaptainOffersPage = lazy(() => import("@/pages/captain/CaptainOffersPage"));
const CaptainScannerPage = lazy(() => import("@/pages/captain/CaptainScannerPage"));
const CaptainGoldenPasses = lazy(() => import("@/pages/captain/CaptainGoldenPasses"));
const CaptainCreateOffer = lazy(() => import("@/pages/captain/CaptainCreateOffer"));
const CaptainEditOffer = lazy(() => import("@/pages/captain/CaptainEditOffer"));

// Admin Dashboard Pages - Lazy loaded
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/components/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminMerchants = lazy(() => import("@/pages/admin/AdminMerchants"));
const AdminOffers = lazy(() => import("@/pages/admin/AdminOffers"));
const AdminCities = lazy(() => import("@/pages/admin/AdminCities"));
const AdminCategories = lazy(() => import("@/pages/admin/AdminCategories"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminAnalytics = lazy(() => import("@/pages/admin/AdminAnalytics"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminExternalAds = lazy(() => import("@/pages/admin/AdminExternalAds"));
const AdminDealBoosts = lazy(() => import("@/pages/admin/AdminDealBoosts"));
const AdminAdConfig = lazy(() => import("@/pages/admin/AdminAdConfig"));
const AdminEvents = lazy(() => import("@/pages/admin/AdminEvents"));
const AdminNotifications = lazy(() => import("@/pages/admin/AdminNotifications"));
const AdminRedemptions = lazy(() => import("@/pages/admin/AdminRedemptions"));
const AdminOfferViews = lazy(() => import("@/pages/admin/AdminOfferViews"));
const AdminGoldenPasses = lazy(() => import("@/pages/admin/AdminGoldenPasses"));
const AdminRewardItems = lazy(() => import("@/pages/admin/AdminRewardItems"));
const AdminRewardBrackets = lazy(() => import("@/pages/admin/AdminRewardBrackets"));
const AdminRewardClaims = lazy(() => import("@/pages/admin/AdminRewardClaims"));

// Legal Pages
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const AccountDeletion = lazy(() => import("@/pages/AccountDeletion"));

// Foreground notification listener component
const ForegroundNotificationListener = ({ children }) => {
  const navigate = useNavigate();
  useEffect(() => {
    const unsubscribe = setupForegroundNotifications(navigate);
    return () => { if (typeof unsubscribe === 'function') unsubscribe(); };
  }, [navigate]);
  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Admin Dashboard Routes - Separate auth context */}
            <Route path="/admin/login" element={
              <AdminProvider>
                <AdminLogin />
              </AdminProvider>
            } />
            <Route path="/admin/*" element={
              <AdminProvider>
                <Routes>
                  <Route element={<AdminLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="merchants" element={<AdminMerchants />} />
                    <Route path="offers" element={<AdminOffers />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="redemptions" element={<AdminRedemptions />} />
                    <Route path="offer-views" element={<AdminOfferViews />} />
                    <Route path="golden-passes" element={<AdminGoldenPasses />} />
                    <Route path="reward-items" element={<AdminRewardItems />} />
                    <Route path="reward-brackets" element={<AdminRewardBrackets />} />
                    <Route path="reward-claims" element={<AdminRewardClaims />} />
                    <Route path="cities" element={<AdminCities />} />
                    <Route path="categories" element={<AdminCategories />} />
                    <Route path="analytics" element={<AdminAnalytics />} />
                    <Route path="external-ads" element={<AdminExternalAds />} />
                    <Route path="deal-boosts" element={<AdminDealBoosts />} />
                    <Route path="ad-config" element={<AdminAdConfig />} />
                    <Route path="events" element={<AdminEvents />} />
                    <Route path="notifications" element={<AdminNotifications />} />
                    <Route path="settings" element={<AdminSettings />} />
                  </Route>
                </Routes>
              </AdminProvider>
            } />

            {/* Customer & Merchant App Routes */}
            <Route path="/*" element={
              <AuthProvider>
                <ForegroundNotificationListener>
                <Routes>
                  {/* Customer App Routes */}
                  <Route path="/" element={<Splash />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/otp-verification" element={<OTPVerification />} />
                  <Route path="/city-selection" element={<CitySelection />} />
                  <Route path="/home" element={<Home />} />
                  <Route path="/deals" element={<VideoDealsScreen />} />
                  <Route path="/offer/:offerId" element={<OfferDetailPage />} />
                  <Route path="/shop/:merchantId" element={<ShopOffersListPage />} />
                  <Route path="/offers/:type" element={<ViewAllOffersPage />} />
                  <Route path="/saved" element={<SavedOffersPage />} />
                  <Route path="/rewards" element={<RewardsPage />} />
                  <Route path="/search-results" element={<SearchResultsPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/event/:eventTag" element={<EventDealsPage />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/account-deletion" element={<AccountDeletion />} />
                  
                  {/* Captain (Merchant) App Routes */}
                  <Route path="/captain/login" element={<CaptainLogin />} />
                  <Route path="/captain/otp-verification" element={<CaptainOTPVerification />} />
                  <Route path="/captain/register" element={<CaptainRegister />} />
                  <Route path="/captain/dashboard" element={<CaptainDashboard />} />
                  <Route path="/captain/profile" element={<CaptainProfilePage />} />
                  <Route path="/captain/offers" element={<CaptainOffersPage />} />
                  <Route path="/captain/offers/new" element={<CaptainCreateOffer />} />
                  <Route path="/captain/offers/edit/:offerId" element={<CaptainEditOffer />} />
                  <Route path="/captain/scanner" element={<CaptainScannerPage />} />
                  <Route path="/captain/golden-passes" element={<CaptainGoldenPasses />} />
                  
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </ForegroundNotificationListener>
              </AuthProvider>
            } />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;
